import { SaudiArabiaFlagIcon } from 'components/icons/SaudiArabiaFlagIcon';
import { UnitedStatesFlagIcon } from 'components/icons/UnitedStatesFlagIcon';
import { enMessages, sarMessages } from 'dictionaries';
import { StringKey } from 'dictionaries/string-key.enum';
import { FC } from 'react';
import {
  HeardFrom,
  IconProps,
  InquiryType,
  LanguageDictionary,
  SectorType,
  TextDirection,
} from 'types';

export enum StorageKey {
  LANGUAGE = 'language',
}

export enum Language {
  ENGLISH = 'ENGLISH',
  ARABIC = 'ARABIC',
}

export const dictionaries: Record<Language, LanguageDictionary> = {
  [Language.ENGLISH]: enMessages,
  [Language.ARABIC]: sarMessages,
};

export const languageDirections: Record<Language, TextDirection> = {
  [Language.ENGLISH]: 'ltr',
  [Language.ARABIC]: 'rtl',
};

export const languagesToLabelMap: Record<Language, string> = {
  [Language.ENGLISH]: 'English',
  [Language.ARABIC]: 'العربية',
};

export const languagesToIconMap: Record<Language, FC<IconProps>> = {
  [Language.ENGLISH]: UnitedStatesFlagIcon,
  [Language.ARABIC]: SaudiArabiaFlagIcon,
};

export const languagesToAbbreviationMap: Record<Language, string> = {
  [Language.ARABIC]: 'sar',
  [Language.ENGLISH]: 'en',
};

export const languagesToISOAbbreviationMap: Record<Language, string> = {
  [Language.ARABIC]: 'ar',
  [Language.ENGLISH]: 'en',
};

export const inquiryTypeToLabelMap: Record<InquiryType, StringKey> = {
  [InquiryType.GENERAL]: StringKey.INQUIRY_GENERAL,
  [InquiryType.DEMO_REQUEST]: StringKey.INQUIRY_DEMO_REQUEST,
  [InquiryType.TECHNICAL_SUPPORT]: StringKey.INQUIRY_TECHNICAL_SUPPORT,
  [InquiryType.SUBSCRIPTION_AND_PRICING]: StringKey.INQUIRY_SUBSCRIPTION_AND_PRICING,
  [InquiryType.PARTNERSHIP_OPPORTUNITIES]: StringKey.INQUIRY_PARTNERSHIP_OPPORTUNITIES,
  [InquiryType.SPONSORSHIP_DEALS]: StringKey.INQUIRY_SPONSORSHIP_DEALS,
  [InquiryType.VENDOR_REGISTRATION]: StringKey.INQUIRY_VENDOR_REGISTRATION,
  [InquiryType.MEDIA_AND_PR]: StringKey.INQUIRY_MEDIA_AND_PR,
  [InquiryType.EVENT_MANAGEMENT_SERVICES]: StringKey.INQUIRY_EVENT_MANAGEMENT_SERVICES,
  [InquiryType.FEEDBACK_OR_SUGGESTIONS]: StringKey.INQUIRY_FEEDBACK_OR_SUGGESTIONS,
  [InquiryType.INVESTOR_RELATIONS]: StringKey.INQUIRY_INVESTOR_RELATIONS,
  [InquiryType.OTHER]: StringKey.INQUIRY_OTHER,
};

export const heardFromToLabelMap: Record<HeardFrom, StringKey> = {
  [HeardFrom.GEA]: StringKey.HEARD_FROM_GEA,
  [HeardFrom.INCUBATORS_AND_ACCELERATORS]: StringKey.HEARD_FROM_INCUBATORS_AND_ACCELERATORS,
  [HeardFrom.PLUG_AND_PLAY]: StringKey.HEARD_FROM_PLUG_AND_PLAY,
  [HeardFrom.SOCIAL_MEDIA]: StringKey.HEARD_FROM_SOCIAL_MEDIA,
  [HeardFrom.FRIEND_OR_COLLEAGUE]: StringKey.HEARD_FROM_FRIEND_OR_COLLEAGUE,
  [HeardFrom.EVENT_OR_EXHIBITION]: StringKey.HEARD_FROM_EVENT_OR_EXHIBITION,
  [HeardFrom.ONLINE_SEARCH]: StringKey.HEARD_FROM_ONLINE_SEARCH,
  [HeardFrom.OTHER]: StringKey.HEARD_FROM_OTHER,
};

export const sectorTypeToLabelMap: Record<SectorType, StringKey> = {
  [SectorType.TECHNOLOGY]: StringKey.SECTOR_TECHNOLOGY,
  [SectorType.FINANCE_AND_INVESTMENT]: StringKey.SECTOR_FINANCE_AND_INVESTMENT,
  [SectorType.ENVIRONMENTAL]: StringKey.SECTOR_ENVIRONMENTAL,
  [SectorType.SOCIAL_AND_CHARITY_IMPACT]: StringKey.SECTOR_SOCIAL_AND_CHARITY_IMPACT,
  [SectorType.GOVERNMENT]: StringKey.SECTOR_GOVERNMENT,
  [SectorType.SCIENCE_AND_EDUCATION]: StringKey.SECTOR_SCIENCE_AND_EDUCATION,
  [SectorType.TRAVEL_AND_TOURISM]: StringKey.SECTOR_TRAVEL_AND_TOURISM,
  [SectorType.MEDIA_AND_COMMUNICATIONS]: StringKey.SECTOR_MEDIA_AND_COMMUNICATIONS,
  [SectorType.CORPORATE_AND_HUMAN_RESOURCES]: StringKey.SECTOR_CORPORATE_AND_HUMAN_RESOURCES,
  [SectorType.HEALTH_AND_WELLNESS]: StringKey.SECTOR_HEALTH_AND_WELLNESS,
  [SectorType.SPORTS_AND_RECREATION]: StringKey.SECTOR_SPORTS_AND_RECREATION,
  [SectorType.ART_AND_CULTURE]: StringKey.SECTOR_ART_AND_CULTURE,
  [SectorType.ENTERTAINMENT_AND_EVENT_MANAGEMENT]:
    StringKey.SECTOR_ENTERTAINMENT_AND_EVENT_MANAGEMENT,
  [SectorType.OTHER]: StringKey.SECTOR_OTHER,
};

export enum RouterKey {
  HOME_PAGE = '/',
  REQUEST_DEMO = '/request-demo',
  REGISTER_NOW = '/register-now',
  TERMS_OF_USE = '/terms-of-use',
  PRIVACY_POLICY = '/privacy-policy',
  EVENT_INVITATION_ACCEPT = '/invitation/accept',
  EVENT_INVITATION_REJECT = '/invitation/reject',
}
//'/landing'
export enum BackendRoute {
  CONTACT_US = '/landing/contact-us',
  JOIN_WAITLIST = '/landing/join-waitlist',
  REQUEST_DEMO = '/landing/request-demo',
  ACCEPT_INVITATION = '/mobile/invitations/accept',
  REJECT_INVITATION = '/mobile/invitations/reject',
}

export const termsOfUseContent: {
  title: StringKey;
  texts: StringKey[];
}[] = [
  {
    title: StringKey.TERMS_ACCEPTANCE_TITLE,
    texts: [
      StringKey.TERMS_ACCEPTANCE_P1,
      StringKey.TERMS_ACCEPTANCE_P2,
      StringKey.TERMS_ACCEPTANCE_P3,
    ],
  },
  {
    title: StringKey.TERMS_DEFINITIONS_TITLE,
    texts: [
      StringKey.TERMS_DEFINITIONS_SYSTEM,
      StringKey.TERMS_DEFINITIONS_USER,
      StringKey.TERMS_DEFINITIONS_ORGANIZER,
      StringKey.TERMS_DEFINITIONS_SPONSOR,
      StringKey.TERMS_DEFINITIONS_EXHIBITOR,
      StringKey.TERMS_DEFINITIONS_VENDOR,
      StringKey.TERMS_DEFINITIONS_SPEAKER,
      StringKey.TERMS_DEFINITIONS_ATTENDEE_VISITOR,
    ],
  },
  {
    title: StringKey.TERMS_REGISTRATION_TITLE,
    texts: [
      StringKey.TERMS_REGISTRATION_REQUIREMENTS,
      StringKey.TERMS_USER_ELIGIBILITY,
      StringKey.TERMS_ELECTRONIC_COMMUNICATIONS,
      StringKey.TERMS_DATA_UPDATES,
    ],
  },
  {
    title: StringKey.TERMS_SUBSCRIPTIONS_TITLE,
    texts: [
      StringKey.TERMS_ORGANIZER_SUBSCRIPTION,
      StringKey.TERMS_TRANSACTION_COMMISSIONS,
      StringKey.TERMS_VENDOR_SPONSOR_FEES,
      StringKey.TERMS_PAYMENT_METHODS,
    ],
  },
  {
    title: StringKey.TERMS_TENDERING_TITLE,
    texts: [
      StringKey.TERMS_TENDERING_P1,
      StringKey.TERMS_TENDERING_P2,
      StringKey.TERMS_TENDERING_P3,
    ],
  },
  {
    title: StringKey.TERMS_PAYMENTS_TITLE,
    texts: [
      StringKey.TERMS_PAYMENTS_P1,
      StringKey.TERMS_PAYMENTS_P2,
      StringKey.TERMS_PAYMENTS_P3,
      StringKey.TERMS_PAYMENTS_P4,
    ],
  },
  {
    title: StringKey.TERMS_INTELLECTUAL_PROPERTY_TITLE,
    texts: [
      StringKey.TERMS_INTELLECTUAL_PROPERTY_P1,
      StringKey.TERMS_INTELLECTUAL_PROPERTY_P2,
      StringKey.TERMS_INTELLECTUAL_PROPERTY_P3,
    ],
  },
  {
    title: StringKey.TERMS_PROHIBITED_ACTIVITIES_TITLE,
    texts: [
      StringKey.TERMS_PROHIBITED_ACTIVITIES_P1,
      StringKey.TERMS_PROHIBITED_ACTIVITIES_BULLET1,
      StringKey.TERMS_PROHIBITED_ACTIVITIES_BULLET2,
      StringKey.TERMS_PROHIBITED_ACTIVITIES_BULLET3,
      StringKey.TERMS_PROHIBITED_ACTIVITIES_BULLET4,
      StringKey.TERMS_PROHIBITED_ACTIVITIES_BULLET5,
      StringKey.TERMS_PROHIBITED_ACTIVITIES_BULLET6,
      StringKey.TERMS_PROHIBITED_ACTIVITIES_BULLET7,
    ],
  },
  {
    title: StringKey.TERMS_PRIVACY_TITLE,
    texts: [StringKey.TERMS_PRIVACY_P1],
  },
  {
    title: StringKey.TERMS_TERM_TERMINATION_TITLE,
    texts: [
      StringKey.TERMS_TERM_TERMINATION_P1,
      StringKey.TERMS_TERM_TERMINATION_P2,
      StringKey.TERMS_TERM_TERMINATION_P3,
      StringKey.TERMS_TERM_TERMINATION_P4,
      StringKey.TERMS_TERM_TERMINATION_P5,
      StringKey.TERMS_TERM_TERMINATION_P6,
    ],
  },
  {
    title: StringKey.TERMS_CHANGES_INTERRUPTS_TITLE,
    texts: [StringKey.TERMS_CHANGES_INTERRUPTS_P1, StringKey.TERMS_CHANGES_INTERRUPTS_P2],
  },
  {
    title: StringKey.TERMS_DISCLAIMER_TITLE,
    texts: [
      StringKey.TERMS_DISCLAIMER_P1,
      StringKey.TERMS_DISCLAIMER_P2,
      StringKey.TERMS_DISCLAIMER_P3,
    ],
  },
  {
    title: StringKey.TERMS_INDEMNIFICATION_TITLE,
    texts: [StringKey.TERMS_INDEMNIFICATION_P1],
  },
  {
    title: StringKey.TERMS_GOVERNING_LAW_TITLE,
    texts: [StringKey.TERMS_GOVERNING_LAW_P1],
  },
  {
    title: StringKey.TERMS_GENERAL_PROVISIONS_TITLE,
    texts: [
      StringKey.TERMS_GENERAL_PROVISIONS_P1,
      StringKey.TERMS_GENERAL_PROVISIONS_P2,
      StringKey.TERMS_GENERAL_PROVISIONS_P3,
      StringKey.TERMS_GENERAL_PROVISIONS_P4,
      StringKey.TERMS_GENERAL_PROVISIONS_P5,
    ],
  },
  {
    title: StringKey.TERMS_LANGUAGE_TITLE,
    texts: [StringKey.TERMS_LANGUAGE_P1],
  },
];

export const privacyPolicyContent: {
  title: StringKey;
  texts: StringKey[];
}[] = [
  {
    title: StringKey.PRIVACY_WHO_WE_ARE_TITLE,
    texts: [StringKey.PRIVACY_DATA_CONTROLLER, StringKey.PRIVACY_POLICY_APPLIES],
  },
  {
    title: StringKey.PRIVACY_LEGAL_BASES_TITLE,
    texts: [
      StringKey.PRIVACY_LEGAL_BASES_P1,
      StringKey.PRIVACY_LEGAL_BASES_A,
      StringKey.PRIVACY_LEGAL_BASES_B,
      StringKey.PRIVACY_LEGAL_BASES_C,
      StringKey.PRIVACY_LEGAL_BASES_D,
    ],
  },
  {
    title: StringKey.PRIVACY_DATA_WE_COLLECT_TITLE,
    texts: [
      StringKey.PRIVACY_DATA_WE_COLLECT_P1,
      StringKey.PRIVACY_DATA_IDENTITY_CONTACT,
      StringKey.PRIVACY_DATA_BUSINESS,
      StringKey.PRIVACY_DATA_ACCOUNT_USAGE,
      StringKey.PRIVACY_DATA_MARKETING,
      StringKey.PRIVACY_DATA_PAYMENT,
      StringKey.PRIVACY_DATA_CONTRACTING,
    ],
  },
  {
    title: StringKey.PRIVACY_HOW_WE_COLLECT_TITLE,
    texts: [
      StringKey.PRIVACY_HOW_WE_COLLECT_P1,
      StringKey.PRIVACY_HOW_DIRECT_INTERACTIONS,
      StringKey.PRIVACY_HOW_AUTOMATED,
      StringKey.PRIVACY_HOW_THIRD_PARTIES,
    ],
  },
  {
    title: StringKey.PRIVACY_PURPOSES_TITLE,
    texts: [
      StringKey.PRIVACY_PURPOSES_P1,
      StringKey.PRIVACY_PURPOSE_OPERATING_ACCOUNTS,
      StringKey.PRIVACY_PURPOSE_MANAGING_OFFERS,
      StringKey.PRIVACY_PURPOSE_PROCESSING_PAYMENTS,
      StringKey.PRIVACY_PURPOSE_CUSTOMER_SUPPORT,
      StringKey.PRIVACY_PURPOSE_IMPROVING_SYSTEM,
      StringKey.PRIVACY_PURPOSE_SECURITY,
      StringKey.PRIVACY_PURPOSE_MARKETING,
      StringKey.PRIVACY_PURPOSE_COMPLIANCE,
    ],
  },
  {
    title: StringKey.PRIVACY_MARKETING_MESSAGES_TITLE,
    texts: [
      StringKey.PRIVACY_MARKETING_MESSAGES_P1,
      StringKey.PRIVACY_MARKETING_MESSAGES_P2,
      StringKey.PRIVACY_MARKETING_MESSAGES_P3,
    ],
  },
  {
    title: StringKey.PRIVACY_COOKIES_TITLE,
    texts: [StringKey.PRIVACY_COOKIES_P1, StringKey.PRIVACY_COOKIES_P2],
  },
  {
    title: StringKey.PRIVACY_DATA_SHARING_TITLE,
    texts: [
      StringKey.PRIVACY_DATA_SHARING_P1,
      StringKey.PRIVACY_DATA_SHARING_PAYMENT_PROCESSORS,
      StringKey.PRIVACY_DATA_SHARING_CONTRACTING_PARTIES,
      StringKey.PRIVACY_DATA_SHARING_AUTHORITIES,
      StringKey.PRIVACY_DATA_SHARING_P2,
    ],
  },
  {
    title: StringKey.PRIVACY_CROSS_BORDER_TITLE,
    texts: [
      StringKey.PRIVACY_CROSS_BORDER_P1,
      StringKey.PRIVACY_CROSS_BORDER_BULLET1,
      StringKey.PRIVACY_CROSS_BORDER_BULLET2,
      StringKey.PRIVACY_CROSS_BORDER_BULLET3,
    ],
  },
  {
    title: StringKey.PRIVACY_INFORMATION_SECURITY_TITLE,
    texts: [
      StringKey.PRIVACY_INFORMATION_SECURITY_P1,
      StringKey.PRIVACY_INFORMATION_SECURITY_P2,
      StringKey.PRIVACY_INFORMATION_SECURITY_ACCESS_CONTROLS,
      StringKey.PRIVACY_INFORMATION_SECURITY_ENCRYPTION,
      StringKey.PRIVACY_INFORMATION_SECURITY_LOGGING,
    ],
  },
  {
    title: StringKey.PRIVACY_RETENTION_TITLE,
    texts: [StringKey.PRIVACY_RETENTION_P1, StringKey.PRIVACY_RETENTION_P2],
  },
  {
    title: StringKey.PRIVACY_YOUR_RIGHTS_TITLE,
    texts: [
      StringKey.PRIVACY_YOUR_RIGHTS_P1,
      StringKey.PRIVACY_YOUR_RIGHTS_ACCESS,
      StringKey.PRIVACY_YOUR_RIGHTS_RECTIFY,
      StringKey.PRIVACY_YOUR_RIGHTS_RESTRICT,
      StringKey.PRIVACY_YOUR_RIGHTS_OBJECT_MARKETING,
      StringKey.PRIVACY_YOUR_RIGHTS_DESTRUCTION,
      StringKey.PRIVACY_YOUR_RIGHTS_WITHDRAW_CONSENT,
      StringKey.PRIVACY_YOUR_RIGHTS_P2,
    ],
  },
  {
    title: StringKey.PRIVACY_BREACH_NOTIFICATION_TITLE,
    texts: [
      StringKey.PRIVACY_BREACH_NOTIFICATION_P1,
      StringKey.PRIVACY_BREACH_NOTIFICATION_AUTHORITY,
      StringKey.PRIVACY_BREACH_NOTIFICATION_INDIVIDUALS,
      StringKey.PRIVACY_BREACH_NOTIFICATION_DETAILS,
      StringKey.PRIVACY_BREACH_NOTIFICATION_P2,
    ],
  },
  {
    title: StringKey.PRIVACY_ELECTRONIC_SIGNATURES_TITLE,
    texts: [StringKey.PRIVACY_ELECTRONIC_SIGNATURES_P1],
  },
  {
    title: StringKey.PRIVACY_LANGUAGE_TITLE,
    texts: [StringKey.PRIVACY_LANGUAGE_P1],
  },
];
